import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, User, Trash2, UserPlus, Copy, Link2,
  UserCheck, ChevronDown, ChevronUp, X, Search,
  Pencil, Check, CreditCard, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";

interface UserRecord {
  id: number;
  email: string;
  name: string | null;
  role: "admin" | "user" | "secretary";
  invite_token: string | null;
  invite_status: "pending" | "claimed" | null;
  card_count: number;
  created_at: string;
  last_signed_in: string | null;
}

interface SecretaryAssignment {
  id: number;
  secretary_id: number;
  user_id: number;
  secretary_email: string;
  secretary_name: string | null;
  user_email: string;
  user_name: string | null;
  assigned_at: string;
}

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  secretary: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  user: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
};

export default function UsersPage() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [assignments, setAssignments] = useState<SecretaryAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // Create form
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"user" | "secretary">("user");
  const [creating, setCreating] = useState(false);

  // Inline edit (pending users only)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editName, setEditName] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Assign secretary
  const [showAssignForm, setShowAssignForm] = useState<number | null>(null);
  const [assignSecretaryId, setAssignSecretaryId] = useState<string>("");

  const LIMIT = 20;

  const loadUsers = useCallback(async (p = page, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (q) params.set("search", q);
      const data = await api.get<{ users: UserRecord[]; total: number }>(`/api/v1/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUsers(page, search);
  }, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 350);
  };

  const handleRoleChange = async (id: number, role: string) => {
    try {
      await api.put(`/api/v1/users/${id}/role`, { role });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role: role as any } : u));
      toast.success("Role updated");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id: number, name: string | null) => {
    if (!confirm(`Delete "${name || "this user"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/v1/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
      setTotal(t => t - 1);
      toast.success("User deleted");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleCreate = async () => {
    if (!newEmail.trim()) { toast.error("Email is required"); return; }
    setCreating(true);
    try {
      const data = await api.post<{ user: UserRecord; inviteUrl: string }>("/api/v1/users", {
        email: newEmail.trim(),
        name: newName.trim() || null,
        role: newRole,
      });
      setUsers(prev => [data.user, ...prev]);
      setTotal(t => t + 1);
      setNewEmail(""); setNewName(""); setNewRole("user");
      setShowCreateForm(false);
      await navigator.clipboard.writeText(`${window.location.origin}${data.inviteUrl}`);
      toast.success("User created — invite link copied to clipboard!");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const copyInviteLink = async (token: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/invite/${token}`);
    toast.success("Invite link copied!");
  };

  const regenerateInvite = async (userId: number) => {
    try {
      const data = await api.post<{ inviteUrl: string }>(`/api/v1/users/${userId}/resend-invite`);
      const newToken = data.inviteUrl.split("/").pop() || "";
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, invite_token: newToken, invite_status: "pending" } : u));
      await navigator.clipboard.writeText(`${window.location.origin}${data.inviteUrl}`);
      toast.success("New invite link generated and copied!");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const startEdit = (u: UserRecord) => {
    setEditingId(u.id);
    setEditEmail(u.email);
    setEditName(u.name || "");
  };

  const saveEdit = async (id: number) => {
    setEditSaving(true);
    try {
      await api.patch(`/api/v1/users/${id}`, { email: editEmail, name: editName || null });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, email: editEmail, name: editName || null } : u));
      setEditingId(null);
      toast.success("User updated");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const loadAssignments = async (userId: number) => {
    setAssignmentsLoading(true);
    try {
      const all = await api.get<SecretaryAssignment[]>("/api/v1/users/secretary-assignments");
      setAssignments(all.filter(a => a.user_id === userId));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const toggleExpand = (userId: number) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setAssignments([]);
    } else {
      setExpandedUser(userId);
      loadAssignments(userId);
    }
  };

  const handleAssignSecretary = async (userId: number) => {
    if (!assignSecretaryId) return;
    try {
      await api.post("/api/v1/users/secretary-assignments", {
        secretaryId: parseInt(assignSecretaryId),
        userId,
      });
      toast.success("Secretary assigned");
      loadAssignments(userId);
      setShowAssignForm(null);
      setAssignSecretaryId("");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleRemoveAssignment = async (userId: number, assignmentId: number) => {
    try {
      await api.delete(`/api/v1/users/secretary-assignments/${assignmentId}`);
      setAssignments(prev => prev.filter(a => a.id !== assignmentId));
      toast.success("Assignment removed");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const secretaries = users.filter(u => u.role === "secretary");
  const totalPages = Math.ceil(total / LIMIT);

  const roleBadge = (role: string) => (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${ROLE_COLORS[role as keyof typeof ROLE_COLORS] || ROLE_COLORS.user}`}>
      {role}
    </span>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} user{total !== 1 ? "s" : ""} · Admin, users, and secretaries
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(v => !v)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors text-sm font-medium shadow-sm"
        >
          <UserPlus className="w-4 h-4" />
          Create User
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-card border border-border rounded-xl p-5 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Create New User</h2>
            <button onClick={() => setShowCreateForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email *</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="user@example.com"
                autoFocus
                className="w-full px-3 py-2.5 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="Jane Doe"
                className="w-full px-3 py-2.5 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "user" | "secretary")}
                className="w-full px-3 py-2.5 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="user">User</option>
                <option value="secretary">Secretary</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {creating ? "Creating..." : "Create & Copy Invite"}
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            An invite link will be generated. Share it with the user — they must sign in via IOPass to claim their account.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-9 pr-4 py-2.5 border border-input rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {/* Users List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <User className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No users match your search" : "No users yet"}</p>
          <p className="text-sm mt-1">{search ? "Try a different query" : "Create the first user above"}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} className="bg-card rounded-xl border border-border overflow-hidden transition-shadow hover:shadow-sm">
              {/* Main row */}
              <div className="p-4 flex items-center gap-3">
                {/* Avatar */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold ${
                  u.role === "admin" ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" :
                  u.role === "secretary" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" :
                  "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                }`}>
                  {u.role === "admin" ? <Shield className="w-4 h-4" /> :
                   u.role === "secretary" ? <UserCheck className="w-4 h-4" /> :
                   <User className="w-4 h-4" />}
                </div>

                {/* Info — editable for pending users */}
                <div className="flex-1 min-w-0">
                  {editingId === u.id ? (
                    <div className="flex gap-2 items-center">
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Name"
                        className="px-2 py-1 border border-input rounded-lg text-sm bg-background w-32 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <input
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        placeholder="Email"
                        className="px-2 py-1 border border-input rounded-lg text-sm bg-background w-48 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <button
                        onClick={() => saveEdit(u.id)}
                        disabled={editSaving}
                        className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        title="Save"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{u.name || <span className="text-muted-foreground italic">Unnamed</span>}</p>
                      {roleBadge(u.role)}
                      {u.invite_status === "pending" && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 uppercase tracking-wide">
                          Invite Pending
                        </span>
                      )}
                    </div>
                  )}
                  {editingId !== u.id && (
                    <div className="flex items-center gap-3 mt-0.5">
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      {u.card_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <CreditCard className="w-3 h-3" />
                          {u.card_count} card{u.card_count !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {editingId !== u.id && (
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Edit pending user */}
                    {u.invite_status === "pending" && (
                      <button
                        onClick={() => startEdit(u)}
                        className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                        title="Edit user details"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Copy invite link (pending only) */}
                    {u.invite_token && u.invite_status === "pending" && (
                      <button
                        onClick={() => copyInviteLink(u.invite_token!)}
                        className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
                        title="Copy invite link"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Regenerate invite (pending only) */}
                    {u.invite_status === "pending" && (
                      <button
                        onClick={() => regenerateInvite(u.id)}
                        className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
                        title="Regenerate invite link"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Role selector */}
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="px-2.5 py-1.5 rounded-lg border border-input bg-background text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="user">User</option>
                      <option value="secretary">Secretary</option>
                      <option value="admin">Admin</option>
                    </select>

                    {/* Expand secretary assignments (for regular users) */}
                    {u.role === "user" && (
                      <button
                        onClick={() => toggleExpand(u.id)}
                        className="p-2 rounded-lg hover:bg-accent text-muted-foreground transition-colors"
                        title="Manage secretary assignments"
                      >
                        {expandedUser === u.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(u.id, u.name)}
                      className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      title="Delete user"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Secretary Assignments Panel */}
              {expandedUser === u.id && u.role === "user" && (
                <div className="border-t border-border bg-muted/20 px-4 py-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assigned Secretaries</h3>
                    {showAssignForm !== u.id ? (
                      <button
                        onClick={() => setShowAssignForm(u.id)}
                        className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        + Assign Secretary
                      </button>
                    ) : (
                      <button
                        onClick={() => { setShowAssignForm(null); setAssignSecretaryId(""); }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    )}
                  </div>

                  {showAssignForm === u.id && (
                    <div className="flex gap-2 mb-3">
                      <select
                        value={assignSecretaryId}
                        onChange={(e) => setAssignSecretaryId(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="">Select secretary...</option>
                        {secretaries.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.name || s.email}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleAssignSecretary(u.id)}
                        disabled={!assignSecretaryId}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                      >
                        Assign
                      </button>
                    </div>
                  )}

                  {secretaries.length === 0 && !assignmentsLoading && (
                    <p className="text-xs text-muted-foreground py-1">
                      No secretary users exist yet. Create a user with the "Secretary" role first.
                    </p>
                  )}

                  {assignmentsLoading ? (
                    <div className="text-xs text-muted-foreground py-2">Loading...</div>
                  ) : assignments.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">No secretaries assigned to this user.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {assignments.map(a => (
                        <div key={a.id} className="flex items-center gap-3 bg-card rounded-lg px-3 py-2 border border-border">
                          <UserCheck className="w-4 h-4 text-blue-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{a.secretary_name || "Unnamed"}</p>
                            <p className="text-xs text-muted-foreground truncate">{a.secretary_email}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveAssignment(u.id, a.id)}
                            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                            title="Remove assignment"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-input hover:bg-accent disabled:opacity-40 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-input hover:bg-accent disabled:opacity-40 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
